import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCheckCircle,
    FiClock,
    FiEye,
    FiFileText,
    FiLoader,
    FiRefreshCw,
    FiSearch,
    FiSlash,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    cancelCreditNote,
    confirmCreditNote,
    fetchCreditNotes,
    creditNoteListSelector,
    creditNoteListSummarySelector,
    creditNoteListPaginationSelector,
    creditNoteLoadingSelector,
    creditNoteErrorSelector,
} from "../../../../../../store/billing/creditNotes/creditNoteSlice";

import CreditNoteDetailsDialog from "./components/CreditNoteDetailsDialog";

import "./CreditNoteListPage.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatStatus = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getCreditAmount = (
    creditNote
) =>
    roundMoney(
        creditNote
            ?.totals
            ?.creditAmount ??
        creditNote
            ?.creditAmount ??
        0
    );

const getCustomerName = (
    creditNote
) =>
    creditNote
        ?.customerSnapshot
        ?.customerName ||
    creditNote
        ?.customerSnapshot
        ?.name ||
    creditNote
        ?.customer
        ?.customerName ||
    creditNote
        ?.customer
        ?.name ||
    "Walk-in Customer";

const getCustomerMobile = (
    creditNote
) =>
    creditNote
        ?.customerSnapshot
        ?.mobile ||
    creditNote
        ?.customer
        ?.mobile ||
    "-";

const getInvoiceId = (
    creditNote
) => {
    const saleInvoice =
        creditNote
            ?.saleInvoice;

    if (
        saleInvoice &&
        typeof saleInvoice ===
            "object"
    ) {
        return (
            saleInvoice?._id ||
            saleInvoice?.id ||
            ""
        );
    }

    return (
        saleInvoice ||
        creditNote
            ?.saleInvoiceId ||
        ""
    );
};

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload
        ?.message ||
    error?.payload ||
    error?.response
        ?.data
        ?.message ||
    fallback;

/*
|--------------------------------------------------------------------------
| STATUS ICON
|--------------------------------------------------------------------------
*/

const StatusIcon = ({
    status,
}) => {
    const value =
        String(
            status ||
                ""
        ).toUpperCase();

    if (
        value ===
        "CONFIRMED"
    ) {
        return (
            <FiCheckCircle />
        );
    }

    if (
        value ===
        "CANCELLED"
    ) {
        return (
            <FiSlash />
        );
    }

    return (
        <FiClock />
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreditNoteListPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const creditNotes =
        useSelector(
            creditNoteListSelector
        ) || [];

    const summary =
        useSelector(
            creditNoteListSummarySelector
        ) || {};

    const pagination =
        useSelector(
            creditNoteListPaginationSelector
        ) || {};

    const loading =
        useSelector(
            creditNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            creditNoteErrorSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        fromDate,
        setFromDate,
    ] = useState("");

    const [
        toDate,
        setToDate,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    /*
    |--------------------------------------------------------------------------
    | DIALOG
    |--------------------------------------------------------------------------
    */

    const [
        selectedCreditNote,
        setSelectedCreditNote,
    ] = useState(null);

    const [
        detailsOpen,
        setDetailsOpen,
    ] = useState(false);

    const [
        actionCreditNoteId,
        setActionCreditNoteId,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD LIST
    |--------------------------------------------------------------------------
    */

    const loadCreditNotes =
        useCallback(
            async () => {
                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        fetchCreditNotes({
                            page,

                            limit,

                            search:
                                search.trim(),

                            status,

                            fromDate:
                                fromDate ||
                                "",

                            toDate:
                                toDate ||
                                "",

                            sortBy:
                                "creditNoteDate",

                            sortOrder:
                                "desc",
                        })
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to load credit notes."
                        )
                    );
                }
            },
            [
                dispatch,
                page,
                limit,
                search,
                status,
                fromDate,
                toDate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | DEBOUNCE SEARCH
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadCreditNotes();
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            loadCreditNotes,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const normalizedSummary =
        useMemo(
            () => ({
                total:
                    toNumber(
                        summary
                            ?.total
                    ),

                draft:
                    toNumber(
                        summary
                            ?.draft
                    ),

                confirmed:
                    toNumber(
                        summary
                            ?.confirmed
                    ),

                cancelled:
                    toNumber(
                        summary
                            ?.cancelled
                    ),

                confirmedCreditAmount:
                    roundMoney(
                        summary
                            ?.confirmedCreditAmount
                    ),

                totalCreditAmount:
                    roundMoney(
                        summary
                            ?.totalCreditAmount
                    ),
            }),
            [
                summary,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | DETAILS
    |--------------------------------------------------------------------------
    */

    const handleOpenDetails = (
        creditNote
    ) => {
        setSelectedCreditNote(
            creditNote
        );

        setDetailsOpen(
            true
        );
    };

    const handleCloseDetails =
        () => {
            setDetailsOpen(
                false
            );

            setSelectedCreditNote(
                null
            );
        };

    /*
    |--------------------------------------------------------------------------
    | FULL VIEW
    |--------------------------------------------------------------------------
    */

    const handleViewPage = (
        creditNote
    ) => {
        if (
            !creditNote?._id
        ) {
            return;
        }

        history.push(
            `/billing/credit-notes/${creditNote._id}`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | VIEW SOURCE INVOICE
    |--------------------------------------------------------------------------
    */

    const handleViewInvoice = (
        creditNote
    ) => {
        const invoiceId =
            getInvoiceId(
                creditNote
            );

        if (
            !invoiceId
        ) {
            setLocalError(
                "Sale invoice ID is missing."
            );

            return;
        }

        history.push(
            `/billing/sale-invoices/${invoiceId}/view`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async (
            creditNote
        ) => {
            if (
                !creditNote?._id
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm credit note ${
                        creditNote
                            ?.creditNoteNumber ||
                        ""
                    }?\n\nStock will be restored and customer ledger credit will be posted.`
                );

            if (
                !approved
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionCreditNoteId(
                    creditNote._id
                );

                await dispatch(
                    confirmCreditNote(
                        creditNote._id
                    )
                ).unwrap();

                await loadCreditNotes();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm credit note."
                    )
                );
            } finally {
                setActionCreditNoteId(
                    ""
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async (
            creditNote
        ) => {
            if (
                !creditNote?._id
            ) {
                return;
            }

            const reason =
                window.prompt(
                    `Enter cancellation reason for ${
                        creditNote
                            ?.creditNoteNumber ||
                        "this credit note"
                    }:`
                );

            if (
                reason ===
                null
            ) {
                return;
            }

            const normalizedReason =
                String(
                    reason ||
                        ""
                ).trim();

            if (
                normalizedReason.length <
                3
            ) {
                setLocalError(
                    "Cancellation reason must contain at least 3 characters."
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionCreditNoteId(
                    creditNote._id
                );

                await dispatch(
                    cancelCreditNote({
                        creditNoteId:
                            creditNote._id,

                        cancellationReason:
                            normalizedReason,
                    })
                ).unwrap();

                await loadCreditNotes();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel credit note."
                    )
                );
            } finally {
                setActionCreditNoteId(
                    ""
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CLEAR FILTERS
    |--------------------------------------------------------------------------
    */

    const handleClearFilters =
        () => {
            setSearch(
                ""
            );

            setStatus(
                ""
            );

            setFromDate(
                ""
            );

            setToDate(
                ""
            );

            setPage(
                1
            );
        };

    /*
    |--------------------------------------------------------------------------
    | DIALOG UPDATED
    |--------------------------------------------------------------------------
    */

    const handleDialogUpdated =
        async () => {
            await loadCreditNotes();
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const totalRecords =
        toNumber(
            pagination
                ?.total,
            0
        );

    const totalPages =
        Math.max(
            toNumber(
                pagination
                    ?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                pagination
                    ?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
        0
            ? (
                  currentPage -
                  1
              ) *
                  limit +
              1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
                limit,
            totalRecords
        );

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="credit-note-list-page">
            {/* HEADER */}

            <header className="credit-note-list-header">
                <div className="credit-note-list-heading">
                    <span className="credit-note-list-heading-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <h1>
                            Credit Notes
                        </h1>

                        <p>
                            Manage sales returns, customer credits and credit adjustments.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="credit-note-list-refresh-button"
                    disabled={
                        loading.list
                    }
                    onClick={
                        loadCreditNotes
                    }
                >
                    <FiRefreshCw
                        className={
                            loading.list
                                ? "credit-note-list-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>
            </header>

            {/* SUMMARY */}

            <section className="credit-note-list-summary">
                <div className="credit-note-list-summary-card">
                    <span>
                        Total Credit Notes
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .total
                        }
                    </strong>

                    <small>
                        All credit notes
                    </small>
                </div>

                <div className="credit-note-list-summary-card credit-note-list-summary-draft">
                    <span>
                        Draft
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .draft
                        }
                    </strong>

                    <small>
                        Awaiting confirmation
                    </small>
                </div>

                <div className="credit-note-list-summary-card credit-note-list-summary-confirmed">
                    <span>
                        Confirmed
                    </span>

                    <strong>
                        {
                            normalizedSummary
                                .confirmed
                        }
                    </strong>

                    <small>
                        Posted credit notes
                    </small>
                </div>

                <div className="credit-note-list-summary-card credit-note-list-summary-credit">
                    <span>
                        Confirmed Credit
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .confirmedCreditAmount
                        )}
                    </strong>

                    <small>
                        Customer credit posted
                    </small>
                </div>
            </section>

            {/* TOOLBAR */}

            <section className="credit-note-list-toolbar credit-note-list-toolbar-expanded">
                <div className="credit-note-list-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder="Search credit note, invoice, customer or mobile..."
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setStatus(
                            event
                                .target
                                .value
                        );
                    }}
                >
                    <option value="">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>
                </select>

                <input
                    type="date"
                    value={
                        fromDate
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setFromDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <input
                    type="date"
                    value={
                        toDate
                    }
                    min={
                        fromDate ||
                        undefined
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setToDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <button
                    type="button"
                    className="credit-note-list-clear-button"
                    onClick={
                        handleClearFilters
                    }
                >
                    Clear
                </button>
            </section>

            {/* ERROR */}

            {(localError ||
                errors.list) && (
                <div className="credit-note-list-alert">
                    {localError ||
                        errors.list}
                </div>
            )}

            {/* TABLE CARD */}

            <section className="credit-note-list-card">
                {loading.list &&
                creditNotes.length ===
                    0 ? (
                    <div className="credit-note-list-loading">
                        <FiLoader className="credit-note-list-spinner" />

                        <strong>
                            Loading credit notes...
                        </strong>
                    </div>
                ) : creditNotes.length ===
                  0 ? (
                    <div className="credit-note-list-empty">
                        <span>
                            <FiFileText />
                        </span>

                        <h3>
                            No credit notes found
                        </h3>

                        <p>
                            Credit notes created from sale invoices will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="credit-note-list-table-wrapper">
                        <table className="credit-note-list-table">
                            <thead>
                                <tr>
                                    <th>
                                        Credit Note
                                    </th>

                                    <th>
                                        Customer
                                    </th>

                                    <th>
                                        Invoice
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Reason
                                    </th>

                                    <th className="text-right">
                                        Amount
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th className="credit-note-list-actions-column">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {creditNotes.map(
                                    (
                                        creditNote,
                                        index
                                    ) => {
                                        const creditStatus =
                                            String(
                                                creditNote
                                                    ?.status ||
                                                    "DRAFT"
                                            ).toUpperCase();

                                        const processing =
                                            actionCreditNoteId ===
                                            creditNote
                                                ?._id;

                                        return (
                                            <tr
                                                key={
                                                    creditNote
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="credit-note-list-number"
                                                        onClick={() =>
                                                            handleViewPage(
                                                                creditNote
                                                            )
                                                        }
                                                    >
                                                        {creditNote
                                                            ?.creditNoteNumber ||
                                                            "-"}
                                                    </button>
                                                </td>

                                                <td>
                                                    <div className="credit-note-list-customer">
                                                        <strong>
                                                            {getCustomerName(
                                                                creditNote
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getCustomerMobile(
                                                                creditNote
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="credit-note-list-invoice"
                                                        onClick={() =>
                                                            handleViewInvoice(
                                                                creditNote
                                                            )
                                                        }
                                                    >
                                                        {creditNote
                                                            ?.saleInvoiceNumber ||
                                                            "-"}
                                                    </button>
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        creditNote
                                                            ?.creditNoteDate
                                                    )}
                                                </td>

                                                <td>
                                                    {formatStatus(
                                                        creditNote
                                                            ?.reason
                                                    )}
                                                </td>

                                                <td className="text-right credit-note-list-amount">
                                                    {formatCurrency(
                                                        getCreditAmount(
                                                            creditNote
                                                        )
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        className={`credit-note-list-status credit-note-list-status-${creditStatus.toLowerCase()}`}
                                                    >
                                                        <StatusIcon
                                                            status={
                                                                creditStatus
                                                            }
                                                        />

                                                        {formatStatus(
                                                            creditStatus
                                                        )}
                                                    </span>
                                                </td>

                                                <td className="credit-note-list-actions-column">
                                                    <div className="credit-note-list-actions">
                                                        <button
                                                            type="button"
                                                            className="credit-note-list-view-button"
                                                            disabled={
                                                                processing
                                                            }
                                                            onClick={() =>
                                                                handleOpenDetails(
                                                                    creditNote
                                                                )
                                                            }
                                                        >
                                                            <FiEye />

                                                            View
                                                        </button>

                                                        {creditStatus ===
                                                            "DRAFT" && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="credit-note-list-confirm-button"
                                                                    disabled={
                                                                        processing
                                                                    }
                                                                    onClick={() =>
                                                                        handleConfirm(
                                                                            creditNote
                                                                        )
                                                                    }
                                                                >
                                                                    {processing ? (
                                                                        <FiLoader className="credit-note-list-spinner" />
                                                                    ) : (
                                                                        <FiCheckCircle />
                                                                    )}

                                                                    Confirm
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="credit-note-list-cancel-button"
                                                                    disabled={
                                                                        processing
                                                                    }
                                                                    onClick={() =>
                                                                        handleCancel(
                                                                            creditNote
                                                                        )
                                                                    }
                                                                >
                                                                    <FiSlash />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}

                <footer className="credit-note-list-pagination">
                    <div className="credit-note-list-pagination-left">
                        <span>
                            Showing{" "}
                            <strong>
                                {startRecord}
                            </strong>
                            {" - "}
                            <strong>
                                {endRecord}
                            </strong>
                            {" of "}
                            <strong>
                                {totalRecords}
                            </strong>
                        </span>

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setPage(
                                    1
                                );

                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );
                            }}
                        >
                            {PAGE_SIZE_OPTIONS.map(
                                (
                                    size
                                ) => (
                                    <option
                                        key={
                                            size
                                        }
                                        value={
                                            size
                                        }
                                    >
                                        {size}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="credit-note-list-pagination-right">
                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                    1 ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.max(
                                        currentPage -
                                            1,
                                        1
                                    )
                                )
                            }
                        >
                            Previous
                        </button>

                        <span>
                            Page{" "}
                            <strong>
                                {currentPage}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {totalPages}
                            </strong>
                        </span>

                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                    totalPages ||
                                loading.list
                            }
                            onClick={() =>
                                setPage(
                                    Math.min(
                                        currentPage +
                                            1,
                                        totalPages
                                    )
                                )
                            }
                        >
                            Next
                        </button>
                    </div>
                </footer>
            </section>

            {/* EXISTING DETAILS DIALOG */}

            <CreditNoteDetailsDialog
                open={
                    detailsOpen
                }
                creditNote={
                    selectedCreditNote
                }
                onClose={
                    handleCloseDetails
                }
                onUpdated={
                    handleDialogUpdated
                }
            />
        </div>
    );
};

export default CreditNoteListPage;